import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Icons } from '@/utils/Icons';
import { useLockBodyScroll } from '@/hooks';
import { DarkFormContext } from './FormTheme';

type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

const SIZE_CLASS: Record<ModalSize, string> = {
  sm: 'sm:max-w-[400px]',
  md: 'sm:max-w-[520px]',
  lg: 'sm:max-w-[720px]',
  xl: 'sm:max-w-[960px]',
};

type ModalProps = {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  /** Small text under the title */
  description?: React.ReactNode;
  size?: ModalSize;
  /** Sticky action row at the bottom */
  footer?: React.ReactNode;
  /** Close when the backdrop is clicked (default true) */
  closeOnBackdrop?: boolean;
  /** Show the ✕ button (default true) */
  showClose?: boolean;
  /** Render as a bottom sheet on small screens (default true) */
  sheetOnMobile?: boolean;
  bodyClassName?: string;
  children: React.ReactNode;
};

/**
 * Shared V2 dark modal: blurred backdrop, rounded panel, title bar with close button,
 * scrollable body and optional footer. Closes on Escape. Bottom sheet on mobile.
 */
const Modal = ({
  open,
  onClose,
  title,
  description,
  size = 'md',
  footer,
  closeOnBackdrop = true,
  showClose = true,
  sheetOnMobile = true,
  bodyClassName = 'p-5',
  children,
}: ModalProps) => {
  useLockBodyScroll(open);

  useEffect(() => {
    // A modal with no close button and no backdrop close is mandatory — Escape can't dismiss it.
    if (!open || (!showClose && !closeOnBackdrop)) return;
    const onKeyDown = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose, showClose, closeOnBackdrop]);

  if (!open) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      onClick={() => closeOnBackdrop && onClose()}
      className={`fixed inset-0 z-[60] flex animate-fade-in justify-center bg-secondary/75 backdrop-blur-sm sm:items-center sm:p-4 ${
        sheetOnMobile ? 'items-end' : 'items-center p-3'
      }`}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`flex max-h-[92vh] w-full flex-col overflow-hidden border border-shell-border bg-shell-panel text-shell-fg shadow-2xl sm:rounded-2xl ${
          SIZE_CLASS[size]
        } ${sheetOnMobile ? 'animate-slide-in-from-bottom rounded-t-2xl sm:animate-fade-in' : 'rounded-2xl'}`}
      >
        {(title || showClose) && (
          <div className="flex shrink-0 items-start justify-between gap-3 border-b border-shell-border px-5 py-4">
            <div className="min-w-0">
              {title && <h2 className="text-base font-semibold text-shell-fg">{title}</h2>}
              {description && <p className="mt-0.5 text-xs text-shell-muted">{description}</p>}
            </div>
            {showClose && (
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="-mr-1 flex size-8 shrink-0 items-center justify-center rounded-full text-shell-muted transition hover:bg-shell-raised hover:text-shell-fg"
              >
                <Icons.Cross size={18} />
              </button>
            )}
          </div>
        )}

        <div className={`min-h-0 flex-1 overflow-y-auto ${bodyClassName}`}>
          <DarkFormContext.Provider value={true}>{children}</DarkFormContext.Provider>
        </div>

        {footer && (
          <div className="flex shrink-0 items-center justify-end gap-2 border-t border-shell-border px-5 py-3">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

/** Matching buttons for modal footers. */
export const ModalButton = ({
  variant = 'primary',
  className = '',
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'danger';
}) => {
  const variants = {
    primary: 'bg-gold text-gold-foreground hover:bg-gold-hover',
    secondary: 'border border-shell-border text-shell-fg hover:border-gold',
    danger: 'bg-danger-red text-body-inverse hover:brightness-110',
  };
  return (
    <button
      type="button"
      {...props}
      className={`flex h-10 items-center justify-center gap-2 rounded-lg px-5 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 ${variants[variant]} ${className}`}
    />
  );
};

/** Dark text input styling shared by modal forms. */
export const modalInputClass =
  'autofill-dark h-11 w-full rounded-lg border border-shell-border bg-shell-raised px-4 text-sm text-shell-fg outline-none transition placeholder:text-shell-muted focus:border-gold';

export default Modal;
